/**
 * Выбор языка интерфейса.
 *
 * Язык — свойство того, кто смотрит, а не организации: за одним и тем же
 * пультом сидят инженер из Кишинёва и подрядчик, читающий по-английски,
 * и общего правильного ответа тут нет. Отсюда хранилище — браузер, как
 * у темы и свёрнутого меню (см. `ui/theme`, `ui/shellPreferences`), и ключ
 * без пользователя: язык — свойство этого рабочего места, и после смены
 * учётной записи на том же ноутбуке он должен остаться прежним (§11,
 * фаза 10: выбор переживает выход и повторный вход).
 */

/** Языки, на которых говорит платформа. Порядок — порядок в переключателе. */
export const LANGUAGES = ['ru', 'en'] as const;

export type Language = (typeof LANGUAGES)[number];

/** Русский по умолчанию (§11, фаза 10, задача 1). */
export const DEFAULT_LANGUAGE: Language = 'ru';

const LANGUAGE_KEY = 'netdeck.shell.lang';

/** Подпись языка на нём самом: «English» в русском меню ищут глазами именно так. */
export const LANGUAGE_NAMES: Readonly<Record<Language, string>> = {
  ru: 'Русский',
  en: 'English',
};

export function isLanguage(value: unknown): value is Language {
  return typeof value === 'string' && (LANGUAGES as readonly string[]).includes(value);
}

/**
 * Что выбрал человек. `null` — переключатель ещё не трогали.
 *
 * Значение, которого мы не писали, считается отсутствующим: сюда попадает
 * и мусор от прошлых версий, и правка руками в консоли, и в обоих случаях
 * честнее спросить браузер, чем спорить с ним.
 */
export function readLanguageChoice(): Language | null {
  try {
    const stored = localStorage.getItem(LANGUAGE_KEY);
    return isLanguage(stored) ? stored : null;
  } catch {
    // Хранилище недоступно — приватный режим, запрет сторонних данных.
    return null;
  }
}

export function writeLanguageChoice(language: Language): void {
  try {
    localStorage.setItem(LANGUAGE_KEY, language);
  } catch {
    // Не сохранилось — переживём: на этом экране язык уже переключён,
    // потеряется только память о выборе к следующей загрузке.
  }
}

/**
 * Язык браузера, если платформа его знает.
 *
 * Смотрим на первые две буквы: `en-GB`, `en-US` и `en` — один и тот же
 * английский, и различать их платформе незачем. Список `languages`
 * перебирается по порядку — он и есть порядок предпочтений человека.
 */
export function browserLanguage(): Language | null {
  const wanted = typeof navigator === 'undefined' ? [] : (navigator.languages ?? []);

  for (const tag of wanted) {
    const base = tag.slice(0, 2).toLowerCase();
    if (isLanguage(base)) {
      return base;
    }
  }

  return null;
}

/**
 * С каким языком открывать приложение.
 *
 * Выбор человека сильнее языка браузера, язык браузера — сильнее умолчания:
 * зашедший впервые с английской системой не должен упираться в русский,
 * а однажды переключившийся не должен переключаться заново.
 */
export function initialLanguage(): Language {
  return readLanguageChoice() ?? browserLanguage() ?? DEFAULT_LANGUAGE;
}
